/**
 * BooksProvider
 * РџСЂРѕРІР°Р№РґРµСЂ РґР»СЏ СЂР°Р±РѕС‚С‹ СЃ РєРЅРёРіР°РјРё РїРѕР»СЊР·РѕРІР°С‚РµР»СЏ
 */

import { createContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Book, CreateBookInput, UpdateBookInput, BookError } from '@/types/book';
import { useAuth } from '@/auth/useAuth';

import { resolveCurrentBook } from './resolveCurrentBook';

interface BooksContextType {
  books: Book[];
  currentBook: Book | null;
  booksLoading: boolean;
  booksError: BookError | null;
  createBook: (input: CreateBookInput) => Promise<Book | null>;
  loadBooks: () => Promise<void>;
  selectBook: (bookId: string) => void;
  saveBook: (bookId: string, updates: UpdateBookInput) => Promise<Book | null>;
}

export const BooksContext = createContext<BooksContextType | undefined>(undefined);

interface BooksProviderProps {
  children: ReactNode;
}

export function BooksProvider({ children }: BooksProviderProps) {
  const { user, profile, profileLoading, updateActiveBookId } = useAuth();
  const [books, setBooks] = useState<Book[]>([]);
  const [currentBook, setCurrentBook] = useState<Book | null>(null);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksError, setBooksError] = useState<BookError | null>(null);

  // Р—Р°РіСЂСѓР·РєР° РєРЅРёРі РїРѕР»СЊР·РѕРІР°С‚РµР»СЏ
  const activeBookId = profile?.active_book_id;
  const loadBooks = useCallback(async () => {
    if (!user) {
      setBooks([]);
      setCurrentBook(null);
      return;
    }

    if (profileLoading) {
      return;
    }

    setBooksLoading(true);
    setBooksError(null);

    try {
      const { data, error } = await supabase
        .from('books')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Books load error:', error);
        setBooksError({ message: 'РќРµ СѓРґР°Р»РѕСЃСЊ Р·Р°РіСЂСѓР·РёС‚СЊ РєРЅРёРіРё', code: error.code });
        setBooks([]);
      } else {
        const nextBooks = data || [];
        setBooks(nextBooks);

        // Р•СЃР»Рё РµСЃС‚СЊ РєРЅРёРіРё Рё РЅРµС‚ РІС‹Р±СЂР°РЅРЅРѕР№ - РІС‹Р±РёСЂР°РµРј РїРµСЂРІСѓСЋ
        // Which book stays selected after a reload. The precedence lives in resolveCurrentBook and
        // it matters: the current selection must outrank the profile's active_book_id, otherwise a
        // deep link is overwritten by a stale profile read (T-D01).
        setCurrentBook((prev) => resolveCurrentBook(prev, nextBooks, activeBookId));
      }
    } catch (err) {
      console.error('Books load exception:', err);
      setBooksError({ message: 'РџСЂРѕРёР·РѕС€Р»Р° РѕС€РёР±РєР° РїСЂРё Р·Р°РіСЂСѓР·РєРµ РєРЅРёРі' });
      setBooks([]);
    } finally {
      setBooksLoading(false);
    }
  }, [activeBookId, profileLoading, user]);

  // РЎРѕР·РґР°РЅРёРµ РЅРѕРІРѕР№ РєРЅРёРіРё
  const createBook = async (input: CreateBookInput): Promise<Book | null> => {
    if (!user) {
      setBooksError({ message: 'РџРѕР»СЊР·РѕРІР°С‚РµР»СЊ РЅРµ Р°РІС‚РѕСЂРёР·РѕРІР°РЅ' });
      return null;
    }

    setBooksError(null);

    try {
      const newBook = {
        user_id: user.id,
        title: input.title,
        theme: input.theme || null,
        status: input.status || 'draft',
        cover_design_state: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('books')
        .insert([newBook])
        .select()
        .single();

      if (error) {
        console.error('Book create error:', error);
        setBooksError({ message: 'РќРµ СѓРґР°Р»РѕСЃСЊ СЃРѕР·РґР°С‚СЊ РєРЅРёРіСѓ', code: error.code });
        return null;
      }

      // Р”РѕР±Р°РІР»СЏРµРј РєРЅРёРіСѓ РІ СЃРїРёСЃРѕРє Рё РґРµР»Р°РµРј РµС‘ С‚РµРєСѓС‰РµР№
      setBooks((prev) => [data, ...prev]);
      setCurrentBook(data);
      void updateActiveBookId(data.id);

      return data;
    } catch (err) {
      console.error('Book create exception:', err);
      setBooksError({ message: 'РџСЂРѕРёР·РѕС€Р»Р° РѕС€РёР±РєР° РїСЂРё СЃРѕР·РґР°РЅРёРё РєРЅРёРіРё' });
      return null;
    }
  };

  // Р’С‹Р±РѕСЂ РєРЅРёРіРё
  const selectBook = (bookId: string) => {
    const book = books.find((candidate) => candidate.id === bookId);
    if (book) {
      setCurrentBook(book);
      void updateActiveBookId(book.id);
    }
  };

  // РЎРѕС…СЂР°РЅРµРЅРёРµ РёР·РјРµРЅРµРЅРёР№ РєРЅРёРіРё
  const saveBook = async (bookId: string, updates: UpdateBookInput): Promise<Book | null> => {
    if (!user) {
      setBooksError({ message: 'РџРѕР»СЊР·РѕРІР°С‚РµР»СЊ РЅРµ Р°РІС‚РѕСЂРёР·РѕРІР°РЅ' });
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('books')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', bookId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Book save error:', error);
        setBooksError({ message: 'РќРµ СѓРґР°Р»РѕСЃСЊ СЃРѕС…СЂР°РЅРёС‚СЊ РєРЅРёРіСѓ', code: error.code });
        return null;
      }

      // РћР±РЅРѕРІР»СЏРµРј РєРЅРёРіСѓ РІ СЃРїРёСЃРєРµ
      setBooks((prev) => prev.map((book) => (book.id === bookId ? data : book)));

      // РћР±РЅРѕРІР»СЏРµРј С‚РµРєСѓС‰СѓСЋ РєРЅРёРіСѓ РµСЃР»Рё СЌС‚Рѕ РѕРЅР°
      if (currentBook?.id === bookId) {
        setCurrentBook(data);
      }

      return data;
    } catch (err) {
      console.error('Book save exception:', err);
      setBooksError({ message: 'РџСЂРѕРёР·РѕС€Р»Р° РѕС€РёР±РєР° РїСЂРё СЃРѕС…СЂР°РЅРµРЅРёРё РєРЅРёРіРё' });
      return null;
    }
  };

  // Р—Р°РіСЂСѓР¶Р°РµРј РєРЅРёРіРё РїСЂРё РјРѕРЅС‚РёСЂРѕРІР°РЅРёРё Рё РїСЂРё РёР·РјРµРЅРµРЅРёРё РїРѕР»СЊР·РѕРІР°С‚РµР»СЏ
  useEffect(() => {
    if (!user) {
      // Сброс книг при выходе пользователя — синхронизация локального состояния с auth
      // (user -> null). Зеркалит guard внутри loadBooks; намеренный side-effect.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBooks([]);
      setCurrentBook(null);
      return;
    }

    if (!profileLoading) {
      loadBooks();
    }
  }, [user, profileLoading, loadBooks]);

  const value = {
    books,
    currentBook,
    booksLoading,
    booksError,
    createBook,
    loadBooks,
    selectBook,
    saveBook,
  };

  return <BooksContext.Provider value={value}>{children}</BooksContext.Provider>;
}
